import { isRouteErrorResponse, Outlet, useRouteError } from 'react-router';
import { Toaster } from 'components/sonner/sonner';

import './app.css';
import './translations/i18n';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostUpdateContainer } from 'components/releaseNotes/PostUpdateContainer';
import { useDocumentScrollable } from 'hooks/useDocumentScrollable';
import {
  AudioDeviceProvider,
  AuthProvider,
  CortiProvider,
  HomeFiltersProvider,
  PatientSyncNavigator,
  PatientSyncProvider,
  QueryProvider,
  SentryErrorBoundary,
} from 'providers';
import { AnalyticsProvider } from 'analytics';
import { initSentry } from '../sentry.config';
import 'utils/environment';
import { AmbossAuthLifecycle, AmbossProvider } from 'widgets/amboss';
import { AudioStatusBar } from 'widgets/audio';

initSentry();

export default function App() {
  useDocumentScrollable();

  return (
    <QueryProvider>
      <AuthProvider>
        <SentryErrorBoundary>
          <AnalyticsProvider>
            <AudioDeviceProvider>
              <HomeFiltersProvider>
                <CortiProvider>
                  <PatientSyncProvider>
                    <PatientSyncNavigator />
                    <AmbossProvider>
                      <AmbossAuthLifecycle />
                      <Outlet />
                      <AudioStatusBar />
                      <Toaster position="top-center" richColors />
                      <PostUpdateContainer />
                      <ReactQueryDevtools initialIsOpen={false} />
                    </AmbossProvider>
                  </PatientSyncProvider>
                </CortiProvider>
              </HomeFiltersProvider>
            </AudioDeviceProvider>
          </AnalyticsProvider>
        </SentryErrorBoundary>
      </AuthProvider>
    </QueryProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (error && error instanceof Error) {
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
